import React from 'react';
import { Typography, Card, Button, Space, Row, Col, Layout, Grid } from 'antd';
import type { AuthenticationResult } from '@azure/msal-browser';
import {
  externalConfig,
  externalInstance,
  handleExternalLoginResult,
  handleUBBLoginResult,
  ubbLoginInstance,
} from '../helper/authHelper';
import '../Assets/Styles/Title.module.css';
import backgroundImage from '../Assets/Images/Babes.png';
import logo from '../Assets/Images/Logo-Babes-Bolyai.png';
import { Content } from 'antd/es/layout/layout';
import { MailOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { AppFooter } from '../Components/AppFooter';
import { AppHeader } from '../Components/AppHeader';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { BASE_PATH } from '../Constants/constants';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useTheme } from '../context/ThemeContext';

const { useBreakpoint } = Grid;

function LoginPage(): React.ReactElement {
  const { t } = useTranslation();
  const loggedInUser = useCurrentUser();
  const queryClient = useQueryClient();
  const screens = useBreakpoint();
  const { isDarkMode } = useTheme();

  if (loggedInUser && loggedInUser.id !== 0) {
    return <Navigate to={`/${BASE_PATH}/${loggedInUser.id}`} replace />;
  }

  const handleUBBLogin = async (): Promise<void> => {
    await ubbLoginInstance
      .loginPopup({ scopes: [] })
      .then(async (result: AuthenticationResult) => {
        await handleUBBLoginResult(result, queryClient);
      })
      .catch((e: unknown) => {
        console.error(e);
      });
  };

  const handleExternalLogin = async (): Promise<void> => {
    await externalInstance
      .loginPopup({
        scopes: externalConfig.auth.scope,
      })
      .then(async (result: AuthenticationResult) => {
        await handleExternalLoginResult(result, queryClient);
      })
      .catch((e: unknown) => {
        console.error(e);
      });
  };

  return (
    <Layout
      style={{
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <AppHeader isLoginPage={true} />

      <Content style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <Row
          justify="center"
          align="middle"
          style={{ height: '100%', position: 'relative', zIndex: 1 }}
        >
          <Col xs={22} sm={18} md={14} lg={10} xl={8}>
            <Card
              style={{
                textAlign: 'center',
                background: isDarkMode
                  ? 'rgba(0, 0, 0, 0.66)'
                  : 'rgba(30, 41, 59, 0.55)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                padding: '20px 10px',
              }}
            >
              <img
                src={logo}
                alt="UBB Logo"
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '16px',
                  filter: 'drop-shadow(0px 4px 6px rgba(0,0,0,0.3))',
                }}
              />

              <Typography.Title
                level={3}
                style={{
                  color: '#ffffff',
                  margin: 0,
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                }}
              >
                {t('loginPage.appLogin')}
              </Typography.Title>

              <Typography.Paragraph
                style={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontSize: '15px',
                  marginTop: '12px',
                  marginBottom: '32px',
                }}
              >
                {t('loginPage.opportunities')}
              </Typography.Paragraph>

              <Space
                direction="vertical"
                size="middle"
                style={{ width: '100%' }}
              >
                {/* UBB Login Button */}
                <Button
                  type="primary"
                  size="large"
                  block
                  shape="round"
                  icon={
                    <SafetyCertificateOutlined style={{ fontSize: '18px' }} />
                  }
                  style={{
                    height: '54px',
                    background: isDarkMode ? '#010e3f' : '#1d4e89',
                    borderColor: '#1d4e89',
                    fontSize: screens.lg ? 16 : 14,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.4)',
                    whiteSpace: 'normal',
                  }}
                  onClick={async () => await handleUBBLogin()}
                >
                  {t('loginPage.loginUbb')}
                </Button>

                {/* External Login Button */}
                <Button
                  size="large"
                  block
                  shape="round"
                  icon={<MailOutlined style={{ fontSize: '18px' }} />}
                  style={{
                    height: '54px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.4)',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'normal',
                  }}
                  onClick={async () => await handleExternalLogin()}
                >
                  {t('loginPage.loginOth')}
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </Content>

      <AppFooter isDark={true} />
    </Layout>
  );
}

export default LoginPage;
